import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Box from '@repo/ui/box'
import Button from '@repo/ui/button'
import { ChevronLeftIcon, PdfIcon, PlusIcon, QuestionMark2Icon } from '@repo/ui/icons'
import Modal, { MODAL_CONTENT_CLASSNAME } from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { API, constructApiUrl } from '../../../api/apis.config'
import { createEndOfJourneyQuestion } from '../../../api/end-of-journey-templates/create-end-of-journey-question'
import { deleteEndOfJourneyQuestions } from '../../../api/end-of-journey-templates/delete-end-of-journey-questions'
import { getEndOfJourneyColumnCodes } from '../../../api/end-of-journey-templates/get-end-of-journey-column-codes'
import { getEndOfJourneyQuestions } from '../../../api/end-of-journey-templates/get-end-of-journey-questions'
import { getEndOfJourneyTemplate } from '../../../api/end-of-journey-templates/get-end-of-journey-template'
import { reorderEndOfJourneyQuestion } from '../../../api/end-of-journey-templates/reOrder-end-of-journey-question'
import { updateEndOfJourneyQuestion } from '../../../api/end-of-journey-templates/update-end-of-journey-question'
import LoadingScreen from '../../../components/elements/loading/loading-screen'
import ManagedDraggableList from '../../../components/elements/managed-draggable-list'
import UserNavBar from '../../../components/elements/nav-bar'
import EndOfJourneyQuestionForm from '../../../components/pages/end-of-journey-question/end-of-journey-question-form'
import { useDownloadDocuments } from '../../../lib/hooks/use-download-documents'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import { EndOfJourneyColumnEnum } from '../../../lib/types/enums/end-of-stay-template/end-of-journey-column.enum'
import { ratingScaleEnumMapping } from '../../../lib/types/enums/end-of-stay-template/rating-scale.enum'
import { EndOfJourneyQuestion } from '../../../lib/types/models/end-of-journey-template/end-of-journey-question'
import { modalButtonConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import NotFoundPage from '../../common/not-found'

const EndOfStayTemplateQuestionsPage = () => {
  const { back } = useNavigator()
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()
  const { downloadDocuments } = useDownloadDocuments()

  const { selectedData, modalState, toggleModal, closeModel } = useModalManager<
    'create' | 'edit',
    EndOfJourneyQuestion
  >(['create', 'edit'])

  const { data: questions } = useQuery({
    queryKey: ['questions', { templateId: id }],
    queryFn: () => {
      if (!id || isNaN(parseInt(id))) throw new Error('Template ID is invalid')
      return getEndOfJourneyQuestions(+id)
    },
    enabled: !!id
  })

  const { data: columnCodes } = useQuery({
    queryKey: ['end-of-journey-column-codes'],
    queryFn: getEndOfJourneyColumnCodes,
    staleTime: Infinity
  })
  const getColumnLabel = (code: EndOfJourneyColumnEnum | null) => {
    const column = columnCodes?.find((columnCode) => columnCode.code === code)
    return column ? `${column.shortLabel} - ${column.label}` : '-'
  }

  const { data: template, isPending: isTemplatePending } = useQuery({
    queryFn: () => {
      if (!id || isNaN(parseInt(id))) throw new Error('Template ID is invalid')
      return getEndOfJourneyTemplate(parseInt(id), 'BASIC')
    },
    queryKey: ['end-of-journey-template', id, 'BASIC'],
    enabled: !!id
  })

  const generateEndOfJourneyTemplatePdfMutation = useMutation({
    mutationFn: async () => {
      if (!id || isNaN(parseInt(id))) throw new Error('Template ID is invalid')
      await downloadDocuments({
        endpoint: constructApiUrl(API['end-of-journey-templates'].generateTemplatePdf.url, { id })
      })
    },
    onError: showAxiosError
  })

  const createEndOfJourneyQuestionMutation = useMutation({
    mutationFn: createEndOfJourneyQuestion,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Action ajoutée avec succès')
      queryClient.invalidateQueries({ queryKey: ['questions', { templateId: id }] })
      closeModel('create')
    }
  })

  const updateEndOfJourneyQuestionMutation = useMutation({
    mutationFn: updateEndOfJourneyQuestion,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Action modifiée avec succès')
      queryClient.invalidateQueries({ queryKey: ['questions', { templateId: id }] })
      closeModel('edit')
    }
  })

  const reorderEndOfJourneyQuestionMutation = useMutation({
    mutationFn: reorderEndOfJourneyQuestion,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Questions réordonnées avec succès')
      queryClient.invalidateQueries({ queryKey: ['questions', { templateId: id }] })
    }
  })

  const deleteEndOfJourneyQuestionMutation = useMutation({
    mutationFn: deleteEndOfJourneyQuestions,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Question supprimée avec succès')
      queryClient.invalidateQueries({ queryKey: ['questions', { templateId: id }] })
    }
  })

  if (isTemplatePending) return <LoadingScreen />
  if (!template) return <NotFoundPage />

  return (
    <div>
      <UserNavBar title={template.name} icon={ChevronLeftIcon} onIconClick={() => back()} />
      <div className="h-4" />
      <Box>
        <div className="flex items-center justify-between">
          <Typography variant="h6" color={tailwindColors.black.DEFAULT}>
            Liste de questions
          </Typography>
          <Button
            label="Voir le PDF"
            icon={PdfIcon}
            variant="ghost"
            loading={generateEndOfJourneyTemplatePdfMutation.isPending}
            onClick={() => {
              generateEndOfJourneyTemplatePdfMutation.mutate()
            }}
          />
        </div>
        <div className="h-2" />
        <Button
          label="Ajouter une question"
          icon={PlusIcon}
          variant="primary"
          onClick={() => toggleModal('create')}
        />
        <div className="h-4" />
        {questions && questions.length > 0 ? (
          <ManagedDraggableList
            items={questions}
            onUpdatePositions={(items) => {
              const reorderedItems = items.map((item) => ({
                questionId: item.id,
                order: item.position
              }))
              reorderEndOfJourneyQuestionMutation.mutate({
                templateId: template.id,
                input: reorderedItems
              })
            }}
            renderContent={(question) => (
              <div className="flex flex-col gap-1">
                <Typography variant="tag" color={tailwindColors.grey.DEFAULT}>
                  {`Q${questions.findIndex((q) => q.id === question.id) + 1}: ${question.question}`}
                </Typography>
                <div className="flex items-center gap-4">
                  <Typography variant="label" color={tailwindColors.grey.lighter}>
                    {`Réponse : ${ratingScaleEnumMapping[question.ratingScale]}`}
                  </Typography>
                  <Typography variant="label" color={tailwindColors.grey.lighter}>
                    {`Colonne : ${getColumnLabel(question.columnCode)}`}
                  </Typography>
                </div>
              </div>
            )}
            onEdit={(question) => toggleModal('edit', question)}
            onDelete={(question) =>
              deleteEndOfJourneyQuestionMutation.mutate({
                templateId: template.id,
                input: { ids: [question.id] }
              })
            }
          />
        ) : (
          <div className="flex items-center justify-center p-4">
            <Typography variant="secondary-body" color={tailwindColors.grey.light.active}>
              Aucune section trouvée
            </Typography>
          </div>
        )}
      </Box>

      <Modal
        isOpen={modalState.create}
        title={'Ajouter une question'}
        icon={QuestionMark2Icon}
        hasBorderBottom
        hideActions
        size="medium"
        onCancel={() => closeModel('create')}
      >
        <EndOfJourneyQuestionForm
          templateId={template.id}
          className={MODAL_CONTENT_CLASSNAME}
          buttonsConfig={modalButtonConfig({
            async onConfirm(values) {
              await createEndOfJourneyQuestionMutation.mutateAsync({
                templateId: template.id,
                ...values
              })
            },
            onCancel: () => closeModel('create')
          })}
        />
      </Modal>

      {selectedData && (
        <Modal
          isOpen={modalState.edit}
          title={'Modifier une question'}
          icon={QuestionMark2Icon}
          hasBorderBottom
          hideActions
          size="medium"
          onCancel={() => closeModel('edit')}
        >
          <EndOfJourneyQuestionForm
            templateId={template.id}
            className={MODAL_CONTENT_CLASSNAME}
            defaultValues={{
              question: selectedData.question,
              ratingScale: selectedData.ratingScale,
              columnCode: selectedData.columnCode ?? undefined
            }}
            buttonsConfig={modalButtonConfig({
              onConfirm(values) {
                return updateEndOfJourneyQuestionMutation.mutateAsync({
                  questionId: selectedData.id,
                  templateId: template.id,
                  ...values
                })
              },
              onCancel: () => closeModel('edit')
            })}
          />
        </Modal>
      )}
    </div>
  )
}

export default EndOfStayTemplateQuestionsPage
